import { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, CheckCircle2, XCircle, Info, X, MapPin, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  fieldPartnersApi,
  type InstallationClaim,
  type ClaimStatus,
} from '@/api/field-partners';
import { formatDate } from '@/utils/formatters';
import { FieldProgramGate } from '@/components/field-partners/FieldProgramGate';
import type { FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

const STATUS_COLORS: Record<ClaimStatus, string> = {
  SUBMITTED: 'bg-blue-50 text-blue-700',
  AUTO_APPROVED: 'bg-emerald-50 text-emerald-700',
  AUTO_REJECTED: 'bg-red-50 text-red-700',
  MANUAL_REVIEW: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-green-50 text-green-700',
  REJECTED: 'bg-red-100 text-red-800',
};

function ClaimsInner({ config }: { config: FieldProgramConfig | null }) {
  const partnerLabel = config?.partnerTypeLabel || 'Field Partner';

  const [rows, setRows] = useState<InstallationClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [selected, setSelected] = useState<InstallationClaim | null>(null);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (filterStatus) params.status = filterStatus;
      const data = await fieldPartnersApi.listClaims(params);
      setRows(data);
    } catch (err: any) {
      if (err.response?.status === 404) setRows([]);
      else setError(err.response?.data?.error || 'Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const act = async (fn: () => Promise<any>) => {
    try {
      await fn();
      setSelected(null);
      setShowReject(false);
      setReason('');
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Action failed');
    }
  };

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.claimNumber.toLowerCase().includes(q) ||
        r.partnerName.toLowerCase().includes(q) ||
        r.customerName.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      pending: rows.filter((r) => r.status === 'MANUAL_REVIEW' || r.status === 'SUBMITTED').length,
      approved: rows.filter((r) => r.status === 'APPROVED' || r.status === 'AUTO_APPROVED').length,
      rejected: rows.filter((r) => r.status === 'REJECTED' || r.status === 'AUTO_REJECTED').length,
    };
  }, [rows]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Installation Claims</h1>
          <p className="text-sm text-[var(--text-muted)]">
            {partnerLabel} installation claims — review photos, GPS, OTP verification
          </p>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total', value: stats.total, color: 'text-[var(--text-primary)]' },
          { label: 'Pending', value: stats.pending, color: 'text-amber-600' },
          { label: 'Approved', value: stats.approved, color: 'text-green-600' },
          { label: 'Rejected', value: stats.rejected, color: 'text-red-600' },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4"
          >
            <p className="text-xs uppercase text-[var(--text-muted)]">{s.label}</p>
            <p className={`mt-1 text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search claim, ${partnerLabel.toLowerCase()}, customer...`}
          className="min-w-[220px] flex-1 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLORS).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-red-600">{error}</div>
        ) : paged.length === 0 ? (
          <div className="py-20 text-center text-sm text-[var(--text-muted)]">No claims</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-inset)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-4 py-3">Claim #</th>
                  <th className="px-4 py-3">{partnerLabel}</th>
                  <th className="px-4 py-3">Unit</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3 text-right">GPS Dist</th>
                  <th className="px-4 py-3">Auto</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {paged.map((c) => (
                  <tr key={c.id} className="hover:bg-[var(--bg-surface-hover)]">
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{c.claimNumber}</td>
                    <td className="px-4 py-3 text-[var(--text-primary)]">{c.partnerName}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">
                      {c.productUnitCode}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{c.customerName}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">
                      {formatDate(c.submittedAt)}
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--text-secondary)]">
                      {c.gpsDistanceM != null ? `${Math.round(c.gpsDistanceM)} m` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-[var(--text-muted)]">{c.autoDecision}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[c.status]}`}
                      >
                        {c.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelected(c)}
                        className="text-sm text-[var(--ink-0)] hover:underline"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div className="flex items-center justify-between border-t border-[var(--border-primary)] px-4 py-2 text-xs text-[var(--text-muted)]">
                <span>
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of{' '}
                  {filtered.length}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded border border-[var(--border-primary)] px-2 py-1 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span>
                    {page} / {pageCount}
                  </span>
                  <button
                    disabled={page === pageCount}
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    className="rounded border border-[var(--border-primary)] px-2 py-1 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                Claim {selected.claimNumber}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="rounded p-1 text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)]"
              >
                <X size={16} />
              </button>
            </div>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto p-6 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">{partnerLabel}</p>
                  <p className="text-[var(--text-primary)]">{selected.partnerName}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">Product Unit</p>
                  <p className="font-mono text-[var(--text-primary)]">
                    {selected.productUnitCode}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">Customer</p>
                  <p className="text-[var(--text-primary)]">
                    {selected.customerName} ({selected.customerPhone})
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">Submitted</p>
                  <p className="text-[var(--text-primary)]">{formatDate(selected.submittedAt)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">GPS Distance</p>
                  <p className="text-[var(--text-primary)]">
                    {selected.gpsDistanceM != null
                      ? `${Math.round(selected.gpsDistanceM)} m`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">OTP Verified</p>
                  <p
                    className={`flex items-center gap-1 ${selected.otpVerified ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {selected.otpVerified ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                    {selected.otpVerified ? 'Yes' : 'No'}
                  </p>
                </div>
                {selected.lat && selected.lng && (
                  <div className="col-span-2">
                    <p className="text-xs uppercase text-[var(--text-muted)]">Location</p>
                    <p className="flex items-center gap-1 text-[var(--text-primary)]">
                      <MapPin size={12} /> {selected.lat.toFixed(5)}, {selected.lng.toFixed(5)}
                    </p>
                  </div>
                )}
              </div>

              {selected.photoUrls.length > 0 && (
                <div>
                  <p className="mb-2 text-xs uppercase text-[var(--text-muted)]">Photos</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.photoUrls.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`claim-${i}`}
                        className="h-32 w-full rounded-lg border border-[var(--border-primary)] object-cover"
                      />
                    ))}
                  </div>
                </div>
              )}

              {selected.flagReason && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                  <strong>Flagged:</strong> {selected.flagReason}
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--border-primary)] px-4 py-3">
              <button
                onClick={() => act(() => fieldPartnersApi.requestClaimInfo(selected.id))}
                className="flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm text-blue-700"
              >
                <Info size={14} /> Request Info
              </button>
              <button
                onClick={() => setShowReject(true)}
                className="flex items-center gap-1 rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm text-red-700"
              >
                <XCircle size={14} /> Reject
              </button>
              <button
                onClick={() => act(() => fieldPartnersApi.approveClaim(selected.id))}
                className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
              >
                <CheckCircle2 size={14} /> Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {showReject && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 shadow-xl">
            <h3 className="mb-2 text-sm font-semibold">Rejection reason</h3>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] p-2 text-sm"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => setShowReject(false)}
                className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  reason.trim()
                    ? act(() => fieldPartnersApi.rejectClaim(selected.id, reason.trim()))
                    : alert('Reason required')
                }
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function InstallationClaimsPage() {
  return <FieldProgramGate>{(config) => <ClaimsInner config={config} />}</FieldProgramGate>;
}

export default InstallationClaimsPage;
